import { useState } from "react";
import { History, Save } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead, Spinner, useFetch } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CsvButton, PeriodPicker, ROLE_NAME, currentPeriod } from "./salesUi";

function HistoryDialog({ uid, period, onClose }) {
  const { data } = useFetch(`/sales/targets/history?user_id=${uid}&period=${period}`);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl" data-testid="target-history-dialog">
        <DialogHeader><DialogTitle>Target changes · {period}</DialogTitle></DialogHeader>
        {!data ? <Spinner /> : !data.length ? <p className="text-sm text-slate-500">No changes yet.</p> : data.map((h) => (
          <p key={h.id} className="border-b border-slate-100 py-2 text-xs"><b>{h.kind}</b>: {h.old ? `${money(h.old.amount)} / ${h.old.count}` : "—"} → <b>{money(h.new.amount)} / {h.new.count}</b> · {h.changed_by_name} · {fmtDateTime(h.created_at)}</p>
        ))}
      </DialogContent>
    </Dialog>
  );
}

function TargetCell({ row, kind, period, editable, onSaved }) {
  const p = kind === "TEAM" ? row.team : row.personal;
  const [v, setV] = useState({ amount: p.target_amount, count: p.target_count });
  const dirty = v.amount !== p.target_amount || v.count !== p.target_count;
  const save = async () => {
    try { await api.put("/sales/targets", { user_id: row.user.id, period, kind, amount: Number(v.amount), count: Number(v.count) }); toast.success("Target saved"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  const tid = `${kind.toLowerCase()}-${row.user.username}`;
  return (
    <td className="min-w-[220px]">
      {editable ? (
        <div className="flex items-center gap-1">
          <input type="number" min={0} className="field w-28 py-1.5 text-xs" value={v.amount} onChange={(e) => setV({ ...v, amount: e.target.value === "" ? "" : Number(e.target.value) })} data-testid={`target-amount-${tid}`} />
          <input type="number" min={0} className="field w-16 py-1.5 text-xs" value={v.count} onChange={(e) => setV({ ...v, count: e.target.value === "" ? "" : Number(e.target.value) })} data-testid={`target-count-${tid}`} />
          {dirty && <button className="btn-primary btn-sm" onClick={save} data-testid={`target-save-${tid}`}><Save size={12} /></button>}
        </div>
      ) : <p className="text-xs">{money(p.target_amount)} / {p.target_count}</p>}
      <p className="mt-1 text-[11px] text-slate-500" data-testid={`target-achieved-${tid}`}>{money(p.achieved_amount)} · {p.achieved_count} sold · <b>{p.pct_amount ?? "—"}%</b> · {money(p.to_go_amount)} to go</p>
    </td>
  );
}

export default function Targets() {
  const { user } = useAuth();
  const isAdmin = ["admin", "super_admin"].includes(user.role);
  const [period, setPeriod] = useState(currentPeriod());
  const [hist, setHist] = useState(null);
  const { data, reload } = useFetch(`/sales/performance?period=${period}`);
  const dash = useFetch(user.role === "sales_manager" ? `/sales/dashboard?period=${period}` : null);
  if (!data) return <Spinner />;
  const rows = data.rows.filter((r) => r.user.active);
  const canEdit = (r) => isAdmin || (r.user.role === "sales_agent" && r.user.manager_id === user.id);
  const csv = rows.map((r) => ({ name: r.user.full_name, role: ROLE_NAME[r.user.role], period, personal_target: r.personal.target_amount, personal_count_target: r.personal.target_count,
    personal_achieved: r.personal.achieved_amount, personal_count: r.personal.achieved_count, team_target: r.team?.target_amount ?? "", team_achieved: r.team?.achieved_amount ?? "" }));
  return (
    <div className="space-y-4">
      <PageHead eyebrow="Sales Force" title="Monthly targets" sub={isAdmin ? "Set Team and Personal targets for managers; you can also set agent targets." : "Divide your team target among your agents. You cannot change your own targets."}
        actions={<div className="flex gap-2"><PeriodPicker value={period} onChange={setPeriod} /><CsvButton name={`giacare-targets-${period}.csv`} rows={csv} /></div>} />
      {dash.data?.unallocated && (
        <p className="rounded-xl bg-purple-50 p-3 text-sm text-purple-900" data-testid="unallocated-target">
          Unallocated team target: <b>{money(dash.data.unallocated.amount)}</b> · <b>{dash.data.unallocated.count}</b> memberships (team target minus personal targets of you and your agents)
        </p>
      )}
      <div className="surface overflow-x-auto">
        <table className="data-table" data-testid="targets-table">
          <thead><tr><th>Name</th><th>Role</th><th>Team target (₱ / count)</th><th>Personal target (₱ / count)</th><th /></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={`${r.user.id}-${period}`}>
              <td><b>{r.user.full_name}</b><br /><span className="text-[10px] text-slate-400">{r.user.manager_name ? `Team: ${r.user.manager_name}` : ""}</span></td>
              <td className="text-xs">{ROLE_NAME[r.user.role]}</td>
              {r.team ? <TargetCell row={r} kind="TEAM" period={period} editable={isAdmin} onSaved={reload} /> : <td className="text-xs text-slate-400">—</td>}
              <TargetCell row={r} kind="PERSONAL" period={period} editable={canEdit(r)} onSaved={reload} />
              <td><button className="btn-ghost btn-sm" onClick={() => setHist(r.user.id)} data-testid={`target-history-${r.user.username}`}><History size={13} /></button></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      {hist && <HistoryDialog uid={hist} period={period} onClose={() => setHist(null)} />}
    </div>
  );
}
